import React, { useState, useEffect } from 'react';
import {
  Box,
  Button, FormControl,
  FormLabel,
  Input,
  Modal,
  ModalClose,
  ModalDialog,
  Stack,
  Table,
  Sheet,
  Typography,
  IconButton,
  Tooltip,
  Select,
  Option,
  Chip,
  Tabs,
  TabList,
  Tab,
  CircularProgress,
  Alert,
  Textarea
} from '@mui/joy';
import { IconWrapper } from './IconWrapper';
import { FaTrash, FaEdit, FaPlus } from 'react-icons/fa';
import { directSupabase } from '../lib/supabaseClient';
import type { Database } from '../types/supabase';
import { normalizeUkDigits } from '../elevate/phone';

type Supplier = Database['public']['Tables']['suppliers']['Row'];
type SupplierInsert = Database['public']['Tables']['suppliers']['Insert'];
type SupplierUpdate = Database['public']['Tables']['suppliers']['Update'];
type SupplierPhoneDraft = { phone: string; label: string };

const emptySupplier: SupplierInsert = {
  supplier_name: '',
  contact_name: '',
  email: '',
  phone: '',
  address: '',
  category: 'Glass',
  notes: ''
};

function missingSupplierPhonesTable(error: { message?: string; code?: string } | null): boolean {
  const text = String(error?.message || '');
  return error?.code === '42P01' || text.includes('supplier_phones');
}

function uniquePhoneDrafts(rows: SupplierPhoneDraft[]): SupplierPhoneDraft[] {
  const seen = new Set<string>();
  return rows.reduce<SupplierPhoneDraft[]>((list, row) => {
    const phone = row.phone.trim();
    if (!phone) return list;
    const key = normalizeUkDigits(phone) || phone;
    if (seen.has(key)) return list;
    seen.add(key);
    list.push({ phone, label: row.label.trim() });
    return list;
  }, []);
}

const SupplierManagement: React.FC = () => {
  const [suppliersList, setSuppliersList] = useState<Supplier[]>([]);
  const [currentSupplier, setCurrentSupplier] = useState<SupplierInsert | SupplierUpdate>(emptySupplier);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [filterCategory, setFilterCategory] = useState<'all' | 'Glass' | 'Windows'>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [phoneDrafts, setPhoneDrafts] = useState<SupplierPhoneDraft[]>([{ phone: '', label: 'Main' }]);
  const [phonesBySupplier, setPhonesBySupplier] = useState<Record<string, string[]>>({});
  const [phonesTableReady, setPhonesTableReady] = useState(true);

  useEffect(() => {
    loadSuppliers();
  }, []);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const { data, error } = await directSupabase
        .from('suppliers')
        .select('*')
        .order('supplier_name', { ascending: true });
      
      if (error) {
        throw new Error(error.message);
      }
      
      setSuppliersList(data || []);
      const extras = await directSupabase
        .from('supplier_phones')
        .select('supplier_id, phone_original');
      if (extras.error && missingSupplierPhonesTable(extras.error)) {
        setPhonesTableReady(false);
        setPhonesBySupplier({});
      } else if (extras.error) {
        throw new Error(extras.error.message);
      } else {
        const grouped: Record<string, string[]> = {};
        (extras.data || []).forEach((row) => {
          const id = String(row.supplier_id);
          const phone = String(row.phone_original || '').trim();
          if (!phone) return;
          grouped[id] = grouped[id] ? [...grouped[id], phone] : [phone];
        });
        setPhonesTableReady(true);
        setPhonesBySupplier(grouped);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load suppliers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddSupplier = () => {
    setCurrentSupplier(emptySupplier);
    setPhoneDrafts([{ phone: '', label: 'Main' }]);
    setIsEditing(false);
    setEditingId(null);
    setIsModalOpen(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    const extras = (phonesBySupplier[supplier.id] || []).filter((phone) => phone !== (supplier.phone || ''));
    const drafts = [
      { phone: supplier.phone || '', label: 'Main' },
      ...extras.map((phone) => ({ phone, label: '' })),
    ].filter((row, index) => row.phone || index === 0);
    setCurrentSupplier({
      supplier_name: supplier.supplier_name,
      contact_name: supplier.contact_name || '',
      email: supplier.email,
      phone: supplier.phone || '',
      address: supplier.address || '',
      category: supplier.category,
      notes: supplier.notes || '',
    });
    setPhoneDrafts(drafts.length ? drafts : [{ phone: '', label: 'Main' }]);
    setEditingId(supplier.id);
    setIsEditing(true);
    setIsModalOpen(true);
  };

  const handleDeleteSupplier = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this supplier?')) return;
    
    try {
      const { error } = await directSupabase
        .from('suppliers')
        .delete()
        .eq('id', id);
      
      if (error) {
        throw new Error(error.message);
      }
      
      await loadSuppliers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete supplier');
    }
  };

  const handleSaveSupplier = async () => {
    if (!currentSupplier.supplier_name || !currentSupplier.email || !currentSupplier.category) {
      alert('Name, email, and category are required!');
      return;
    }
    try {
      const phones = uniquePhoneDrafts(phoneDrafts);
      const primaryPhone = phones[0]?.phone || currentSupplier.phone || null;
      const supplierData = {
        supplier_name: currentSupplier.supplier_name,
        contact_name: currentSupplier.contact_name || null,
        email: currentSupplier.email,
        phone: primaryPhone,
        address: currentSupplier.address || null,
        notes: currentSupplier.notes || null,
        category: currentSupplier.category,
      };
      let supplierId = editingId;
      if (isEditing && editingId) {
        const { error } = await directSupabase
          .from('suppliers')
          .update(supplierData)
          .eq('id', editingId);
        
        if (error) {
          throw new Error(error.message);
        }
      } else {
        const { data, error } = await directSupabase
          .from('suppliers')
          .insert([supplierData])
          .select('id')
          .single();
        
        if (error) {
          throw new Error(error.message);
        }
        supplierId = data?.id || null;
      }
      if (supplierId && phonesTableReady) {
        const removed = await directSupabase.from('supplier_phones').delete().eq('supplier_id', supplierId);
        if (removed.error && !missingSupplierPhonesTable(removed.error)) {
          throw new Error(removed.error.message);
        }
        if (!removed.error && phones.length > 0) {
          const inserted = await directSupabase.from('supplier_phones').insert(
            phones.map((row, index) => ({
              supplier_id: supplierId,
              phone_original: row.phone,
              phone_normalized: normalizeUkDigits(row.phone),
              label: row.label || (index === 0 ? 'Main' : null),
            })),
          );
          if (inserted.error && !missingSupplierPhonesTable(inserted.error)) {
            throw new Error(inserted.error.message);
          }
        }
      }
      await loadSuppliers();
      setIsModalOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save supplier');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setCurrentSupplier(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const filteredSuppliers = suppliersList.filter(supplier =>
    filterCategory === 'all' || supplier.category === filterCategory
  );

  const getCategoryColor = (category: 'Glass' | 'Windows') => {
    switch (category) {
      case 'Glass': return 'primary';
      case 'Windows': return 'success';
      default: return 'neutral';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={2}>
        <Alert variant="soft" color="danger">
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography level="h4">Supplier Management</Typography>
        <Button
          startDecorator={<IconWrapper icon={FaPlus} />}
          onClick={handleAddSupplier}
        >
          Add Supplier
        </Button>
      </Box>

      <Tabs 
        value={filterCategory} 
        onChange={(_, value) => setFilterCategory(value as 'all' | 'Glass' | 'Windows')}
        sx={{ mb: 2 }}
      >
        <TabList>
          <Tab value="all">All Suppliers</Tab>
          <Tab value="Glass">Glass Suppliers</Tab>
          <Tab value="Windows">Window Suppliers</Tab>
        </TabList>
      </Tabs>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Sheet sx={{ height: '500px', overflow: 'auto' }}>
        <Table stickyHeader>
          <thead>
            <tr>
              <th>Supplier Name</th>
              <th>Contact Name</th>
              <th style={{ minWidth: 180 }}>Email</th>
              <th style={{ minWidth: 120 }}>Phone</th>
              <th>Address</th>
              <th>Category</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center' }}>
                  <CircularProgress size="sm" />
                </td>
              </tr>
            ) : filteredSuppliers.length === 0 ? (
              <tr>
                <td colSpan={8} style={{ textAlign: 'center' }}>
                  <Typography level="body-sm">No suppliers found.</Typography>
                </td>
              </tr>
            ) : (
              filteredSuppliers.map((supplier) => (
                <tr key={supplier.id}>
                  <td>{supplier.supplier_name}</td>
                  <td>{supplier.contact_name || '—'}</td>
                  <td style={{ minWidth: 180, wordBreak: 'break-all', overflowWrap: 'anywhere' }}>{supplier.email}</td>
                  <td style={{ minWidth: 120, wordBreak: 'break-all' }}>
                    {(phonesBySupplier[supplier.id] || (supplier.phone ? [supplier.phone] : [])).join(' · ') || '—'}
                  </td>
                  <td>{supplier.address || '—'}</td>
                  <td>
                    <Chip 
                      size="sm" 
                      variant="soft" 
                      color={getCategoryColor(supplier.category as 'Glass' | 'Windows')}
                    >
                      {supplier.category}
                    </Chip>
                  </td>
                  <td>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Edit">
                        <IconButton 
                          size="sm" 
                          variant="plain" 
                          color="neutral" 
                          onClick={() => handleEditSupplier(supplier)}
                        >
                          <IconWrapper icon={FaEdit} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="sm" 
                          variant="plain" 
                          color="danger" 
                          onClick={() => handleDeleteSupplier(supplier.id)}
                        >
                          <IconWrapper icon={FaTrash} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </Sheet>

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <ModalDialog
          aria-labelledby="supplier-modal-title"
          aria-describedby="supplier-modal-description"
        >
          <ModalClose onClick={() => setIsModalOpen(false)} />
          <Typography id="supplier-modal-title" level="h4" mb={2}>
            {isEditing ? 'Edit Supplier' : 'Add New Supplier'}
          </Typography>
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Supplier Name</FormLabel>
              <Input
                name="supplier_name"
                value={currentSupplier.supplier_name}
                onChange={handleInputChange}
                autoFocus
              />
            </FormControl>
            <FormControl>
              <FormLabel>Contact Name</FormLabel>
              <Input
                name="contact_name"
                value={currentSupplier.contact_name || ''}
                onChange={handleInputChange}
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Email</FormLabel>
              <Input
                name="email"
                type="email"
                value={currentSupplier.email || ''}
                onChange={handleInputChange}
              />
            </FormControl>
            <FormControl>
              <FormLabel>Telephone numbers</FormLabel>
              <Stack spacing={1}>
                {phoneDrafts.map((row, index) => (
                  <Stack key={index} direction="row" spacing={1} alignItems="center">
                    <Input
                      placeholder={index === 0 ? 'Main number' : 'Additional number'}
                      value={row.phone}
                      onChange={(event) => {
                        const value = event.target.value;
                        setPhoneDrafts((prev) => prev.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, phone: value } : item
                        )));
                        if (index === 0) {
                          setCurrentSupplier((prev) => ({ ...prev, phone: value }));
                        }
                      }}
                      sx={{ flex: 1 }}
                    />
                    <Input
                      placeholder="Label"
                      value={row.label}
                      onChange={(event) => {
                        const value = event.target.value;
                        setPhoneDrafts((prev) => prev.map((item, itemIndex) => (
                          itemIndex === index ? { ...item, label: value } : item
                        )));
                      }}
                      sx={{ width: 110 }}
                    />
                    {phoneDrafts.length > 1 && (
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        aria-label="Remove telephone number"
                        onClick={() => setPhoneDrafts((prev) => prev.filter((_, itemIndex) => itemIndex !== index))}
                      >
                        <IconWrapper icon={FaTrash} />
                      </IconButton>
                    )}
                  </Stack>
                ))}
                <Button
                  size="sm"
                  variant="plain"
                  startDecorator={<IconWrapper icon={FaPlus} />}
                  onClick={() => setPhoneDrafts((prev) => [...prev, { phone: '', label: '' }])}
                >
                  Add another number
                </Button>
              </Stack>
            </FormControl>
            <FormControl required>
              <FormLabel>Address</FormLabel>
              <Input
                name="address"
                value={currentSupplier.address || ''}
                onChange={handleInputChange}
              />
            </FormControl>
            <FormControl required>
              <FormLabel>Category</FormLabel>
              <Select
                name="category"
                value={currentSupplier.category === 'Windows' || currentSupplier.category === 'Glass' ? currentSupplier.category : 'Glass'}
                onChange={(_: React.SyntheticEvent | null, value: string | null) => value && setCurrentSupplier(prev => ({ ...prev, category: value as 'Glass' | 'Windows' }))}
              >
                <Option value="Glass">Glass</Option>
                <Option value="Windows">Windows</Option>
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel>Notes</FormLabel>
              <Textarea
                name="notes"
                value={currentSupplier.notes || ''}
                onChange={handleInputChange}
                minRows={2}
              />
            </FormControl>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="plain"
                color="neutral"
                onClick={() => setIsModalOpen(false)}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveSupplier}>
                {isEditing ? 'Save Changes' : 'Add Supplier'}
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default SupplierManagement; 